export const queryKeys = {
  auth: {
    index: 'auth',
  },
  countries: {
    index: 'countries',
    all: () => [queryKeys.countries.index],
  },
  user: {
    index: 'user',
    current: () => [queryKeys.user.index, 'current'],
  },
  carts: {
    index: 'carts',
    all: () => [queryKeys.carts.index],
    cart: (cartId: number) => [queryKeys.carts.index, cartId],
    user: (userId: number) => [queryKeys.carts.index, 'user', userId],
    adminPatientCarts: (userId?: number, page?: number) => [
      queryKeys.carts.index,
      'admin-patient-carts',
      userId,
      page,
    ],
    patientCarts: (doctorId?: number, page?: number) => [
      queryKeys.carts.index,
      'patient-carts',
      doctorId,
      page,
    ],
  },
  items: {
    index: 'items',
    all: () => [queryKeys.items.index],
    item: (itemId: number) => [queryKeys.items.index, itemId],
  },
  parcels: {
    index: 'parcels',
    listTerminals: (countryCode?: string) => [
      queryKeys.parcels.index,
      'terminals',
      countryCode ?? 'LT',
    ],
  },
  promoCodes: {
    index: 'promo-codes',
    all: (page?: number) =>
      page !== undefined
        ? [queryKeys.promoCodes.index, 'all', page]
        : [queryKeys.promoCodes.index, 'all'],
  },
  clinics: {
    index: 'clinics',
    all: (page?: number, perPage?: number) =>
      page !== undefined
        ? [queryKeys.clinics.index, 'all', page, perPage]
        : [queryKeys.clinics.index, 'all'],
  },
  users: {
    index: 'users',
    all: (page?: number, filters?: Record<string, unknown>) =>
      page !== undefined
        ? [queryKeys.users.index, 'all', page, filters]
        : [queryKeys.users.index, 'all', filters],
    detail: (id: number) => [queryKeys.users.index, id],
  },
  orders: {
    index: 'orders',
    all: (page?: number, filters?: Record<string, unknown>) =>
      page !== undefined
        ? [queryKeys.orders.index, 'all', page, filters]
        : [queryKeys.orders.index, 'all'],
    order: (orderId: number) => [queryKeys.orders.index, orderId],
    label: (orderId: number, itemIds?: Array<number>) => [
      queryKeys.orders.index,
      orderId,
      'label',
      itemIds ?? [],
    ],
    history: () => [queryKeys.orders.index, 'history'],
  },
  ingredients: {
    index: 'ingredients',
    all: () => [queryKeys.ingredients.index],
    ingredient: (ingredientId: number) => [
      queryKeys.ingredients.index,
      'single',
      ingredientId,
    ],
  },
  protocols: {
    index: 'protocols',
    all: () => [queryKeys.protocols.index],
    protocol: (protocolId: number) => [queryKeys.protocols.index, protocolId],
  },
  adminIngredients: {
    index: 'admin-ingredients',
    all: (page?: number | string) =>
      page !== undefined
        ? [queryKeys.adminIngredients.index, 'all', page]
        : [queryKeys.adminIngredients.index, 'all'],
    single: (id: number) => [queryKeys.adminIngredients.index, id],
    categories: () => [queryKeys.adminIngredients.index, 'categories'],
  },
  invitations: {
    index: 'invitations',
    all: (role?: string) => [queryKeys.invitations.index, role],
    detail: (uuid: string) => [queryKeys.invitations.index, uuid],
  },
  warningPatterns: {
    index: 'warning-patterns',
    all: (page?: number) =>
      page !== undefined
        ? [queryKeys.warningPatterns.index, 'all', page]
        : [queryKeys.warningPatterns.index, 'all'],
  },
  stock: {
    index: 'stock',
    all: (page?: number, filter?: string, search?: string) => [
      queryKeys.stock.index,
      'all',
      page,
      filter,
      search,
    ],
  },
  adminProtocols: {
    index: 'admin-protocols',
    all: (page?: number) =>
      page !== undefined
        ? [queryKeys.adminProtocols.index, 'all', page]
        : [queryKeys.adminProtocols.index, 'all'],
    single: (id: number) => [queryKeys.adminProtocols.index, id],
  },
  adminItems: {
    index: 'admin-items',
    all: (page?: number) =>
      page !== undefined
        ? [queryKeys.adminItems.index, 'all', page]
        : [queryKeys.adminItems.index, 'all'],
    single: (id: number) => [queryKeys.adminItems.index, id],
  },
  adminBenefits: {
    index: 'admin-benefits',
    all: (page?: number) =>
      page !== undefined
        ? [queryKeys.adminBenefits.index, 'all', page]
        : [queryKeys.adminBenefits.index, 'all'],
  },
  doctorPatients: {
    index: 'doctor-patients',
    all: (filters?: Record<string, unknown>) => [
      queryKeys.doctorPatients.index,
      'all',
      filters,
    ],
  },
  adminQrCodes: {
    index: 'admin-qr-codes',
    all: (page?: number, filters?: Record<string, unknown>) =>
      page !== undefined
        ? [queryKeys.adminQrCodes.index, 'all', page, filters]
        : [queryKeys.adminQrCodes.index, 'all'],
  },
  adminQrCodeNotes: {
    index: 'admin-qr-code-notes',
    all: (page?: number, filters?: Record<string, unknown>) =>
      page !== undefined
        ? [queryKeys.adminQrCodeNotes.index, 'all', page, filters]
        : [queryKeys.adminQrCodeNotes.index, 'all'],
    single: (id: number) => [queryKeys.adminQrCodeNotes.index, id],
  },
  adminUserQrNotes: {
    index: 'admin-user-qr-notes',
    all: (page?: number, filters?: Record<string, unknown>) =>
      page !== undefined
        ? [queryKeys.adminUserQrNotes.index, 'all', page, filters]
        : [queryKeys.adminUserQrNotes.index, 'all'],
  },
  doctorQrCodes: {
    index: 'doctor-qr-codes',
    all: (doctorId?: number) => [
      queryKeys.doctorQrCodes.index,
      'all',
      doctorId,
    ],
  },
  adminMaintenance: {
    index: 'admin-maintenance',
    status: () => [queryKeys.adminMaintenance.index, 'status'],
  },
  adminStats: {
    index: 'admin-stats',
    doctors: (filters?: Record<string, unknown>) => [
      queryKeys.adminStats.index,
      'doctors',
      filters,
    ],
    clinics: (filters?: Record<string, unknown>) => [
      queryKeys.adminStats.index,
      'clinics',
      filters,
    ],
    users: () => [queryKeys.adminStats.index, 'users'],
    orders: (filters?: Record<string, unknown>) => [
      queryKeys.adminStats.index,
      'orders',
      filters,
    ],
    orderItems: (filters?: Record<string, unknown>) => [
      queryKeys.adminStats.index,
      'order-items',
      filters,
    ],
  },
};
